
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Crown, Upload, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useImageUpload } from '@/hooks/useImageUpload';

interface FounderFeatureRequestProps {
  onClose: () => void;
}

const FounderFeatureRequest: React.FC<FounderFeatureRequestProps> = ({ onClose }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const { imageUrl, uploading, handleUpload } = useImageUpload();
  
  const [formData, setFormData] = useState({
    founder_name: '',
    age: '',
    linkedin_url: '',
    startup_organization_name: '',
    startup_type: '',
    success_story: '',
    how_teenvisor_helps: ''
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUploadWrapper = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleUpload(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to submit a request",
        variant: "destructive"
      });
      return;
    }

    if (!formData.founder_name.trim() || !formData.age.trim() || !formData.success_story.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from("founder_feature_requests")
        .insert({
          user_id: user.id,
          founder_name: formData.founder_name.trim(),
          age: parseInt(formData.age),
          linkedin_url: formData.linkedin_url.trim() || null,
          profile_picture_url: imageUrl || null,
          startup_organization_name: formData.startup_organization_name.trim(),
          startup_type: formData.startup_type.trim(),
          success_story: formData.success_story.trim(),
          how_teenvisor_helps: formData.how_teenvisor_helps.trim(),
          status: 'pending'
        });

      if (error) {
        throw error;
      }

      toast({
        title: "Request Submitted",
        description: "Your founder feature request has been submitted for review"
      });

      onClose();
    } catch (error) {
      console.error("Error submitting request:", error);
      toast({
        title: "Error",
        description: "Failed to submit request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto border-2 border-blue-200 bg-blue-50/50">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
              <Crown className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-xl text-blue-700 text-center">Request to be Featured as Founder</CardTitle>
          <CardDescription className="text-blue-600 text-center">
            Share your entrepreneurial journey and inspire other young innovators
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-blue-700">Founder Name *</label>
                <Input
                  value={formData.founder_name}
                  onChange={(e) => handleInputChange('founder_name', e.target.value)}
                  placeholder="Your full name"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-blue-700">Age *</label>
                <Input
                  type="number"
                  value={formData.age}
                  onChange={(e) => handleInputChange('age', e.target.value)}
                  placeholder="Your age"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-blue-700">LinkedIn Profile</label>
              <Input
                value={formData.linkedin_url}
                onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-blue-700">Profile Picture</label>
              <div className="flex items-center gap-4">
                {imageUrl && (
                  <img src={imageUrl} alt="Profile" className="w-16 h-16 rounded-full object-cover" />
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUploadWrapper}
                    className="hidden"
                    id="profile-upload"
                  />
                  <label htmlFor="profile-upload">
                    <Button type="button" variant="outline" disabled={uploading} className="cursor-pointer" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload Photo'}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-blue-700">Startup/Organization Name</label>
                <Input
                  value={formData.startup_organization_name}
                  onChange={(e) => handleInputChange('startup_organization_name', e.target.value)}
                  placeholder="Name of your startup or organization"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-blue-700">Type of Startup</label>
                <Input
                  value={formData.startup_type}
                  onChange={(e) => handleInputChange('startup_type', e.target.value)}
                  placeholder="e.g., Tech, Healthcare, Education"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-blue-700">Story Behind Your Success *</label>
              <Textarea
                value={formData.success_story}
                onChange={(e) => handleInputChange('success_story', e.target.value)}
                placeholder="Share your entrepreneurial journey, challenges overcome, and achievements..."
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-blue-700">How Can TeenVisor Help You?</label>
              <Textarea
                value={formData.how_teenvisor_helps}
                onChange={(e) => handleInputChange('how_teenvisor_helps', e.target.value)}
                placeholder="Describe how TeenVisor can support your growth and help you reach more young entrepreneurs..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1 border-blue-300 text-blue-600 hover:bg-blue-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default FounderFeatureRequest;
